import * as React from 'react'
import moment from 'moment'
import styled from 'styled-components'
import { IWindow } from 'static/interfaces'
import { IJob, IJobSlot, IAgentRank, ISkill } from 'static/interfaces'
import * as constants from 'static/constants'
import { jobService } from 'core/services'
import SvgIcon from 'components/atoms/SvgIcon'
import { Select, Button, CheckBox } from 'components/atoms'
import TourSkillSingleSelect from 'components/molecules/TourSkill/TourSkillSingleSelect'
import JobSlot from 'components/organisms/Job/JobSlot'
import { defaultJobSlot, getJobStatus } from 'utils/job'
import { IBuildedTour } from 'utils/weeklyTypes'

declare var window: IWindow

interface IProps {
  tour: IBuildedTour
  job: IJob | null
  tourSkills: Array<{ id: number; name: string }>
  specialRanks: IAgentRank[]
  // GlobalModal内(WeeklyProviderツリー外)ではStaticContextが届かないため、JobSlot用のスキルもpropsで受け取る
  skills: ISkill[]
  setCreating: React.Dispatch<React.SetStateAction<boolean>>
  isCreating: boolean
  // GlobalModal内(WeeklyProviderツリー外)でも使えるようcontextではなくpropsで受け取る
  refetchTour: (tourId: number) => void
}

const JOB_REQUIRED_FIELDS = ['publish_start_at', 'is_draft']
const JOB_SLOT_REQUIRED_FIELDS = ['tour_skill_min_level', 'tour_skill_max_level']

const Job: React.VFC<IProps> = ({
  tour,
  job: initialJob,
  tourSkills,
  specialRanks,
  skills,
  setCreating,
  isCreating,
  refetchTour,
}) => {
  const [job, setJob] = React.useState<IJob>(
    initialJob && !isCreating ? initialJob : { ...initialJob, publish_end_at: null }
  )
  const [jobSlots, setJobSlots] = React.useState<IJobSlot[]>(initialJob?.job_slots || [])
  const [tourSkillId, setTourSkillId] = React.useState<number | null>(
    initialJob?.job_slots?.[0]?.tour_skill_id ?? tourSkills[0]?.id ?? null
  )
  // tourSkills は初回読込時に[]になることがあるので再読み込みで再度セットする
  React.useEffect(() => {
    if (tourSkills.length > 0 && tourSkillId === null) {
      setTourSkillId(initialJob?.job_slots?.[0]?.tour_skill_id ?? tourSkills[0].id)
    }
  }, [tourSkills, tourSkillId, initialJob?.job_slots])

  const [editting, setEditting] = React.useState<boolean>(isCreating ? true : false)
  const status = React.useMemo(() => getJobStatus(job), [job.publish_start_at, job.publish_end_at])
  const submittable = React.useMemo(
    () =>
      JOB_REQUIRED_FIELDS.every(
        field => job[field] !== null && job[field] !== undefined && job[field] !== ''
      ) &&
      jobSlots.every(slot =>
        JOB_SLOT_REQUIRED_FIELDS.every(
          field => slot[field] !== null && slot[field] !== undefined && slot[field] !== ''
        )
      ) &&
      tourSkillId !== null,
    [job, jobSlots, tourSkillId]
  )
  // refetchTour対応
  React.useEffect(() => {
    if (!isCreating && initialJob) {
      setJob(initialJob)
      setJobSlots(initialJob.job_slots || [])
      if (initialJob.job_slots?.[0]?.tour_skill_id != null) {
        setTourSkillId(initialJob.job_slots[0].tour_skill_id)
      }
    }
  }, [initialJob?.id, initialJob?.job_bulk_plan_id, initialJob?.has_bulk_diff, isCreating])

  const PUBLISH_END_AT_DEFAULT = React.useMemo(
    () =>
      moment(tour.day)
        .startOf('day')
        .subtract(1, 'day')
        .add(20, 'hours')
        .format('YYYY-MM-DD HH:mm'),
    [tour.day]
  )

  const handleSubmit = React.useCallback(async () => {
    try {
      const params = {
        job_attributes: {
          ...(({ job_slots, agent_rank, ...rest }) => rest)(job),
          publish_start_at: moment(job.publish_start_at).format('YYYY-MM-DD HH:mm'),
          publish_end_at: job.publish_end_at
            ? moment(job.publish_end_at).format('YYYY-MM-DD HH:mm')
            : PUBLISH_END_AT_DEFAULT,
          has_bulk_diff: job.job_bulk_plan_id ? true : false,
        },
        job_slots_attributes: jobSlots.map(slot => ({
          ...slot,
          tour_skill_id: tourSkillId,
          direct_max_limit: slot.direct_max_limit || 0,
          selection_max_limit: slot.selection_max_limit || 0,
          allow_selection: (slot.selection_max_limit || 0) > 0,
        })),
      }
      const { job: savedJob, flush } = initialJob?.id
        ? await jobService.updateJob(initialJob.id, params)
        : await jobService.createJob(params)
      window.flashMessages.addMessage({
        text: flush.message,
        type: flush.type,
      })
      if (savedJob) {
        // 表示用のランクはサーバ解決のため、保存結果から反映する
        setJob(prev => ({ ...prev, agent_rank: savedJob.agent_rank }))
      }
      refetchTour(tour.id)
      setEditting(false)
      setCreating(false)
    } catch (e) {
      console.error(e)
    }
  }, [tour, job, jobSlots, tourSkillId, isCreating, initialJob?.id, setCreating, refetchTour])

  const handleDelete = React.useCallback(async () => {
    if (!initialJob?.id || !window.confirm('ガイド募集情報を削除してよろしいですか？')) {
      return
    }
    try {
      const { flush } = await jobService.deleteJob(initialJob.id)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      setEditting(false)
      refetchTour(tour.id)
    } catch (e) {
      console.error(e)
    }
  }, [initialJob, tour, refetchTour])

  const isBulk = React.useMemo(() => {
    return job.job_bulk_plan_id && !job.has_bulk_diff
  }, [job.job_bulk_plan_id, job.has_bulk_diff])

  // 無効化済みランクが設定されているJobでも表示・選択が壊れないよう、現在のランクを選択肢に含める
  const rankOptions = React.useMemo(() => {
    const options = specialRanks.map(rank => ({ value: String(rank.id), label: rank.name }))
    if (job.agent_rank_id != null && !specialRanks.some(rank => rank.id === job.agent_rank_id)) {
      options.unshift({
        value: String(job.agent_rank_id),
        label: job.agent_rank?.name || `ID: ${job.agent_rank_id}`,
      })
    }
    return options
  }, [specialRanks, job.agent_rank_id, job.agent_rank])

  return (
    <Wrapper className={`jobContainer ${status}`}>
      <div className={`${editting ? 'edit' : 'show'} ${isBulk ? 'isBulk' : ''}`}>
        {editting ? (
          <>
            <div className="formContainer">
              <div className="editInner">
                <TourSkillSingleSelect
                  className="tourSkillSelect"
                  options={tourSkills.map(skill => ({
                    label: skill.name,
                    value: String(skill.id),
                  }))}
                  value={tourSkillId != null ? String(tourSkillId) : null}
                  onChange={value => setTourSkillId(value ? Number(value) : null)}
                />
                {(specialRanks.length > 0 || job.agent_rank_id != null) && (
                  <div className="specialRankContainer">
                    <CheckBox
                      name="is_special_rank"
                      label="特殊"
                      checked={job.agent_rank_id != null}
                      showLabelInline={true}
                      onChangeHandler={e =>
                        setJob({
                          ...job,
                          agent_rank_id: e.target.checked ? specialRanks[0]?.id ?? null : null,
                        })
                      }
                    />
                    {job.agent_rank_id != null && (
                      <Select
                        name="agent_rank_id"
                        options={rankOptions}
                        value={String(job.agent_rank_id)}
                        onChangeHandler={e =>
                          setJob({ ...job, agent_rank_id: Number(e.target.value) })
                        }
                      />
                    )}
                  </div>
                )}
                <div className="publishedAtContainer">
                  <label>公開日時</label>
                  <input
                    required={true}
                    name="publish_start_at"
                    type="datetime-local"
                    value={moment(job.publish_start_at).format('YYYY-MM-DDTHH:mm')}
                    className="publishedAtDate"
                    onChange={e => setJob({ ...job, publish_start_at: e.target.value })}
                  />
                </div>
                <div className="publishedAtContainer">
                  <label>締切日時</label>
                  <input
                    required={true}
                    name="publish_end_at"
                    type="datetime-local"
                    value={moment(job.publish_end_at).format('YYYY-MM-DDTHH:mm')}
                    className="publishedAtDate"
                    onChange={e => setJob({ ...job, publish_end_at: e.target.value })}
                  />
                </div>
                <div className="publishStatus">
                  <button
                    className={`isNotDraft ${job.is_draft ? '' : 'selected'}`}
                    onClick={() => setJob({ ...job, is_draft: false })}
                  >
                    公開
                  </button>
                  <button
                    className={`isDraft ${job.is_draft ? 'selected' : ''}`}
                    onClick={() => setJob({ ...job, is_draft: true })}
                  >
                    非公開
                  </button>
                </div>
              </div>
              {jobSlots.map((jobSlot, _index) => (
                <JobSlot
                  index={_index}
                  key={jobSlot.id || jobSlot.client_uid || _index}
                  slot={jobSlot}
                  setSlots={setJobSlots}
                  editting={editting}
                  skills={skills}
                />
              ))}
              <div className="addSlotButton">
                <button onClick={() => setJobSlots(prev => [...prev, defaultJobSlot()])}>
                  <SvgIcon name="add" className="icon" ariaHidden={true} role="presentation" />
                  詳細な設定を追加する
                </button>
              </div>
            </div>
            <div className="buttonContainer">
              <Button
                small={true}
                handleClick={handleSubmit}
                primary={true}
                disabled={!submittable}
              >
                保存する
              </Button>
              <Button
                small={true}
                handleClick={() => {
                  setEditting(false)
                  setCreating(false)
                  setJobSlots(initialJob?.job_slots || [])
                  setTourSkillId(initialJob?.job_slots?.[0]?.tour_skill_id || null)
                }}
              >
                キャンセル
              </Button>
            </div>
            {job?.id && (
              <button type="button" className="deleteButton" onClick={handleDelete}>
                <SvgIcon
                  name="delete_forever"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
                削除する
              </button>
            )}
          </>
        ) : (
          <div className={`${status} ${isBulk ? 'isBulk' : ''} jobInfo`}>
            {!isBulk && job.is_draft && (
              <div className="isDraft">
                <SvgIcon name="block" className="icon" ariaHidden={true} role="presentation" />
                非公開
              </div>
            )}
            <div className="tourSkillLabel">
              {constants.JOB_STATUS_LABELS[status]} {isBulk ? '- 一斉 ' : ''}-{' '}
              {tourSkills.find(tourSkill => tourSkill.id === tourSkillId)?.name}
            </div>
            {job.agent_rank && <div className="agentRankLabel">{job.agent_rank.name}</div>}
            {status === 'upcoming' ? (
              <div className="publishedAtDate">
                公開期間 : {moment(job.publish_start_at).format('YYYY/MM/DD, H:mm')} ~{' '}
                {moment(job.publish_end_at).format('YYYY/MM/DD, H:mm')}
              </div>
            ) : (
              <div className="publishedAtDate">
                締切日時: {moment(job.publish_end_at).format('YYYY/MM/DD, H:mm')}
              </div>
            )}
            <div className="editButton" onClick={() => setEditting(true)}>
              <SvgIcon name="edit" className="icon" ariaHidden={true} role="presentation" />
              編集
            </div>
            <div className="jobSlots">
              {jobSlots.map((jobSlot, _index) => (
                <JobSlot
                  index={_index}
                  key={jobSlot.id || jobSlot.client_uid || _index}
                  slot={jobSlot}
                  setSlots={setJobSlots}
                  editting={editting}
                  skills={skills}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  > * {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
  }
  & + .jobContainer {
    margin-top: 4px;
  }

  .isNotDraft.selected,
  .isDraft.selected {
    color: ${constants.COLORS.primary};
    background-color: ${constants.COLORS.primary}3d;
  }

  .show {
    .jobInfo {
      position: relative;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px 12px;
      width: 100%;
      &.open,
      &.upcoming,
      &.ended {
        line-height: 2rem;
        padding: 4px 64px 4px 4px;
        border-radius: 4px;
      }
      &.upcoming {
        color: ${constants.COLORS.subText};
        background-color: ${constants.COLORS.lightGray};
      }
      &.open {
        color: ${constants.COLORS.primary};
        background-color: ${constants.COLORS.infoBG2};
      }
      &.ended {
        color: ${constants.COLORS.subText};
        background-color: ${constants.COLORS.disabled};
        .tourSkillLabel {
          background-color: ${constants.COLORS.subText};
        }
      }
      &.open.isBulk {
        color: ${constants.COLORS.black};
        background-color: ${constants.COLORS.successBG2};
        .tourSkillLabel {
          background-color: ${constants.COLORS.primary};
        }
      }
      .icon {
        width: 16px;
        height: 16px;
      }
    }
    .isDraft {
      display: flex;
      align-items: center;
      gap: 4px;
      color: ${constants.COLORS.white};
      background-color: ${constants.COLORS.danger};
      font-size: 12px;
      line-height: 16px;
      border-radius: 12px;
      padding: 4px 6px;
      margin-right: -8px;
      .icon {
        width: 16px;
        height: 16px;
      }
    }
    .tourSkillLabel {
      font-size: 12px;
      line-height: 16px;
      padding: 4px 8px;
      color: ${constants.COLORS.white};
      background-color: ${constants.COLORS.info2};
      border-radius: 12px;
    }
    .agentRankLabel {
      margin-left: -8px;
      font-size: 12px;
      line-height: 16px;
      padding: 3px 8px;
      color: ${constants.COLORS.info2};
      background-color: ${constants.COLORS.white};
      border: 1px solid ${constants.COLORS.info2};
      border-radius: 12px;
    }
    .publishedAtDate {
      font-size: 12px;
      color: ${constants.COLORS.black};
    }
    .editButton {
      position: absolute;
      top: 8px;
      right: 8px;
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      color: ${constants.COLORS.primary};
      font-size: 12px;
      transition: 0.2s color;
      .icon {
        width: 16px;
        height: 16px;
      }
      &:hover {
        color: ${constants.COLORS.secondary};
      }
    }
  }

  .edit {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-direction: column;
    padding: 8px;
    border-radius: 8px;
    background-color: ${constants.COLORS.lightGray};

    .formContainer {
      display: flex;
      flex-wrap: wrap;
      flex-direction: column;
      gap: 8px;
      width: 100%;
    }
    .editInner {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px 12px;
    }

    .publishedAtDate {
      width: 180px;
      height: 32px;
      padding: 4px 8px;
      border: 1px solid ${constants.COLORS.divider};
      border-radius: 4px;
      font-size: 14px;
      &:focus {
        outline: none;
        border-color: ${constants.COLORS.primary};
      }
    }
    .publishedAtContainer {
      display: flex;
      align-items: center;
      gap: 8px;
      label {
        font-size: 13px;
        white-space: nowrap;
      }
      input {
        font-size: 13px;
      }
    }
    .publishStatus {
      display: flex;
      align-items: center;
      gap: 0;
      button {
        min-width: 60px;
        height: 32px;
        padding: 4px 12px;
        color: ${constants.COLORS.subText};
        border: 1px solid ${constants.COLORS.divider};
        border-radius: 16px;
        font-size: 12px;
        background-color: ${constants.COLORS.white};
        cursor: pointer;
        transition: 0.2s all;
        &:first-child {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          border-right: none;
        }
        &:last-child {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }
        &:hover {
          color: ${constants.COLORS.primary};
        }
        &.selected {
          font-weight: 600;
          color: ${constants.COLORS.white};
          background-color: ${constants.COLORS.primary};
        }
      }
    }

    .selectContainer {
      display: flex;
      align-items: center;
      gap: 8px;
      .selectLabel {
        font-size: 12px;
        white-space: nowrap;
      }
    }
    .Select_Main {
      ::before,
      ::after {
        border-width: 4px;
      }
      select {
        font-size: 14px;
        height: 32px;
        background-color: ${constants.COLORS.white};
        padding: 2px 28px 2px 8px;
      }
    }

    .slotEdit {
      max-width: 520px;
      padding: 8px;
      border-radius: 8px;
      background-color: ${constants.COLORS.lightGray};
      border: 1px solid ${constants.COLORS.divider};
      transition: 0.2s background-color;
      .selectContainer {
        width: 100%;
      }
      &:has(.deleteSlotButton button:hover) {
        background-color: ${constants.COLORS.divider2};
      }
    }

    // react-select overrides
    .css-tlfecz-indicatorContainer,
    .css-1gtu0rj-indicatorContainer {
      padding: 0 8px;
    }
    .skills__control {
      min-height: 32px !important;
      height: 32px !important;
      min-width: 240px;
    }
    .tourSkillSelect {
      font-size: 14px;
      flex-grow: 1;
      max-width: 400px;
    }
    .tourSkillSingleSelect__control {
      min-height: 32px !important;
      height: 32px !important;
      min-width: 160px;
    }

    .specialRankContainer {
      display: flex;
      align-items: center;
      gap: 8px;
      .CheckBox_Main > label {
        font-weight: normal;
      }
    }

    .addSlotButton {
      height: 20px;
      button {
        display: inline-flex;
        align-items: center;
        justify-content: flex-start;
        gap: 4px;
        line-height: 20px;
        height: 20px;
        padding: 0;
        color: ${constants.COLORS.primary};
        background-color: none;
        font-size: 13px;
        transition: 0.2s color;
        cursor: pointer;
        border: none;
        &:hover {
          color: ${constants.COLORS.secondary};
        }
        .icon {
          width: 20px;
          height: 20px;
        }
      }
    }

    .buttonContainer {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 8px;
      width: 100%;
      border-top: 1px dashed ${constants.COLORS.divider};
      padding-top: 8px;
      button {
        min-width: 88px;
        height: 32px;
        margin-left: 0;
      }
    }
    .deleteButton {
      position: absolute;
      bottom: 12px;
      right: 12px;
      display: flex;
      align-items: center;
      gap: 4px;
      line-height: 20px;
      padding: 0;
      font-size: 13px;
      color: ${constants.COLORS.subText};
      background-color: transparent;
      border: none;
      cursor: pointer;
      transition: 0.2s color;
      .icon {
        width: 20px;
        height: 20px;
      }
      &:hover {
        color: ${constants.COLORS.cautionText};
      }
    }
  }
  .jobSlots {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
  }
`

export default Job
